<template>
  <div class="trim-info">
    <template v-if="isStandardArtwork">
      <RemoveBackground
        v-if="enableAbAiMonetization && canRemoveBg && !hasGangSheetError"
        :value="removeBackground"
        :disabled="!enableBackgroundRemovalToggle"
        :class="{
          'trim-info__remove-toggle--disabled': !enableBackgroundRemovalToggle
        }"
        @change="handleRemoveBackgroundChange"
      />
      <button
        v-else-if="canRemoveBg && !hasGangSheetError"
        type="button"
        class="trim-info__remove-toggle"
        :class="{
          'trim-info__remove-toggle--selected': removeBackground,
          'trim-info__remove-toggle--disabled': !enableBackgroundRemovalToggle
        }"
        @click="handleRemoveBackgroundChange"
      >
        <UseSvg class="trim-info__remove-toggle-icon" icon="check" />
        <span class="trim-info__remove-toggle-label">Remove Background</span>
      </button>
    </template>
    <template v-else-if="!isStandardArtwork">
      <CheckBox
        v-if="canRemoveBg && !hasGangSheetError"
        label="Remove Background"
        :style="'gray'"
        :disable="!enableBackgroundRemovalToggle"
      >
        <input
          type="checkbox"
          :checked="removeBackground"
          :disabled="!enableBackgroundRemovalToggle"
          @change="handleRemoveBackgroundChange"
        />
      </CheckBox>
    </template>
    <CheckBox
      v-else-if="allowForceBgRemove"
      label="Remove Background"
      :style="'gray'"
      :disabled="!enableBackgroundRemovalToggle"
    >
      <input
        type="checkbox"
        :checked="userForceBgRemove"
        :disabled="!enableBackgroundRemovalToggle"
        @change="handleManualBgRemoval"
      />
    </CheckBox>
    <div v-if="toggleBackgroundError" class="trim-info__wrapper trim-info__wrapper--error">
      <UseSvg icon="warning" class="trim-info__icon" />
      <span>
        {{ toggleBackgroundError }}
      </span>
    </div>
  </div>
</template>

<script>
import { findIndexById } from "common/utils/helpers/find-index-by-id";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import UseSvg from "common/vue/components/UseSvg.vue";
import CheckBox from "components/jiffy/check-box/CheckBox.vue";
import { api } from "jiffy-api";
import debounce from "lodash.debounce";
import { mapActions, mapState } from "pinia";
import RemoveBackground from "./ArtworkQualityConfigPanel/components/RemoveBackground.vue";
import { useSpanglesStore } from "./spangles-module";
import { useStickersStore } from "./stickers-module";
import { useDtfStore } from "./store";
import { hasGangSheetError } from "./utils";

const REMOVE_BACKGROUND_SWITCH_DEBOUNCE = 3000;

export default {
  name: "TrimInfo",
  components: { CheckBox, UseSvg, RemoveBackground },
  props: {
    toggleBackgroundError: {
      type: String,
      default: null
    },
    removeBackground: {
      type: Boolean,
      default: true
    },
    defaultRemoveBackground: {
      type: Boolean,
      default: true
    },
    canRemoveBg: {
      type: Boolean,
      default: false
    },
    id: {
      type: Number,
      required: true
    },
    paddingSize: {
      type: String,
      default: "no_padding"
    },
    artwork: {
      type: Object,
      default: null
    },
    activeConfigSection: {
      type: [String, undefined],
      default: null
    }
  },
  data() {
    return {
      backgroundRemovedSwitch: true,
      userForceBgRemove: false,
      annotateMode: "keep"
    };
  },
  computed: {
    ...mapState(useDtfStore, [
      "sticker",
      "allowForceBgRemove",
      "productId",
      "experiments",
      "isProColor",
      "isSpangle",
      "isStandardArtwork",
      "enableAbAiMonetization",
      "artworks",
      "businessLine"
    ]),
    enableBackgroundRemovalToggle() {
      return this.isStandardArtwork ? this.artwork.enhancementCompleted : true;
    },
    hasGangSheetError() {
      return hasGangSheetError(this.artwork);
    },
    annotateEnabled() {
      return this.artwork.annotateEnabled;
    },
    undoEnabled() {
      const { revisionImageUrls, revisionPosition } = this.artwork;
      return revisionPosition > 0 && revisionImageUrls[revisionPosition - 1] !== undefined;
    },
    redoEnabled() {
      const { revisionImageUrls, revisionPosition } = this.artwork;
      return (
        revisionPosition < revisionImageUrls.length - 1 &&
        revisionImageUrls[revisionPosition + 1] !== undefined
      );
    }
  },
  watch: {
    annotateMode() {
      this.handleAnnotateModeChange();
    },
    activeConfigSection(section) {
      if (section === "jiffy-print-perfect" && !this.removeBackground) {
        this.handleRemoveBackgroundChange(true);
      }
    }
  },
  beforeMount() {
    this.updateToggleBackground = debounce(
      this.updateToggleBackground,
      REMOVE_BACKGROUND_SWITCH_DEBOUNCE
    );

    if (this.enableAbAiMonetization) {
      if (this.activeConfigSection === "jiffy-print-perfect" && !this.removeBackground) {
        this.handleRemoveBackgroundChange(true);
      }
    }
  },
  methods: {
    ...mapActions(useDtfStore, [
      "updateArtworkStyle",
      "sendUpdateArtworkStyle",
      "loadArtworkAndSetPaddingImage",
      "generateCmykImages",
      "generateSpangleImage",
      "updateArtworkValuesById",
      "setPendingPinningInvalidation"
    ]),
    ...mapState(useDtfStore, ["preCut"]),
    ...mapActions(useStickersStore, ["generateStickerImage"]),
    ...mapActions(useSpanglesStore, ["resetSpangles"]),
    handleRemoveBackgroundChange(arg) {
      const isManuallyToggled = typeof arg !== "boolean";
      const newValue = isManuallyToggled ? !this.removeBackground : arg;

      // Intercept if there's a completed pinning session
      if (this.artwork?.pinningSessionId) {
        if (arg?.target) {
          arg.target.checked = this.removeBackground;
        }
        this.setPendingPinningInvalidation({
          type: "removeBackground",
          artworkId: this.artwork.id,
          value: newValue
        });
        return;
      }

      setTimeout(() => {
        this.executeRemoveBackgroundChange(newValue, isManuallyToggled);
      }, 0);
    },
    executeRemoveBackgroundChange(isBgRemoved, isManuallyToggled = false) {
      this.backgroundRemovedSwitch = isBgRemoved;
      const { id } = this;
      const artworkIndex = findIndexById(this.artworks, id);
      const artwork = this.artworks[artworkIndex];

      if (this.isStandardArtwork && !artwork.enhancementCompleted) {
        return;
      }

      submitDtfEvent("remove_background_toggled", {
        artwork_id: this.artwork.id,
        product_id: this.productId,
        previous_state: this.removeBackground,
        new_state: isBgRemoved,
        active_config_section: this.activeConfigSection,
        business_line: this.businessLine,
        is_manually_toggled: isManuallyToggled
      });

      this.resetSpangles();
      this.updateArtworkStyle({ id, isBgRemoved });

      if (this.sticker) {
        this.generateStickerImage({
          artwork: this.artwork,
          removeBackground: isBgRemoved
        });
      }

      this.updateToggleBackground(isBgRemoved);
    },
    handleManualBgRemoval(event) {
      this.userForceBgRemove = event.target.checked;

      this.handleRemoveBackgroundChange(event);

      submitDtfEvent("manual_bg_removal_toggle", {
        artwork: this.artwork
      });
    },
    updateToggleBackground(removed) {
      const paddingSize = this.artwork.paddingSize || "no_padding";
      this.sendUpdateArtworkStyle({
        id: this.id,
        isBgRemoved: removed,
        padding_size: paddingSize,
        forceBgRemoval: this.userForceBgRemove
      });
    },
    handleAnnotateClick(value) {
      this.updateArtworkValuesById({ id: this.id, annotateEnabled: value });
    },
    handleAnnotateModeChange() {
      this.updateArtworkValuesById({ id: this.id, annotateMode: this.annotateMode });
    },
    async segmentImage() {
      if (this.artwork.annotatedPoints.length === 0) return;

      const response = await api.dtf.segmentImage({
        id: this.id,
        points: this.artwork.annotatedPoints,
        canvas_width: this.artwork.annotatedCanvasWidth,
        canvas_height: this.artwork.annotatedCanvasHeight,
        product_id: this.productId
      });

      if (this.isProColor) {
        await this.generateCmykImages({ artwork: this.artwork });
      }

      if (this.isSpangle) {
        await this.generateSpangleImage({ artwork: this.artwork });
      }

      const updatedAttributes = {
        url: response.image_url,
        metadata: response.metadata,
        revisionImageUrls: response.revision_image_urls,
        revisionPosition: response.revision_image_urls.length - 1,
        annotatedPoints: [],
        originalImageUrl: response.original_image_url,
        originalNoBgUrl: response.original_no_bg_url,
        stickerCutlineNoBgUrl: response.sticker_cutline_no_bg_url,
        stickerCutlineWithBgUrl: response.sticker_cutline_with_bg_url
      };
      this.updateArtworkValuesById({ id: this.id, ...updatedAttributes });

      if (this.sticker) {
        const updatedArtwork = { ...this.artwork, ...updatedAttributes };
        await this.generateStickerImage({ artwork: updatedArtwork, useCache: false });
      }
    },
    async handleRevisionToggle(revisionType) {
      const { revisionPosition } = this.artwork;
      const newPosition = revisionType === "undo" ? revisionPosition - 1 : revisionPosition + 1;

      const response = await api.dtf.toggleSegmentVersion({
        id: this.id,
        position: newPosition,
        toggle_type: revisionType,
        product_id: this.productId
      });

      if (this.isProColor) {
        await this.generateCmykImages({ artwork: this.artwork });
      }

      if (this.isSpangle) {
        await this.generateSpangleImage({ artwork: this.artwork });
      }

      const updatedAttributes = {
        url: response.image_url,
        metadata: response.metadata,
        revisionPosition: newPosition,
        originalImageUrl: response.original_image_url,
        originalNoBgUrl: response.original_no_bg_url,
        stickerCutlineNoBgUrl: response.sticker_cutline_no_bg_url,
        stickerCutlineWithBgUrl: response.sticker_cutline_with_bg_url
      };
      this.updateArtworkValuesById({ id: this.id, ...updatedAttributes });

      if (this.sticker) {
        const updatedArtwork = { ...this.artwork, ...updatedAttributes };
        await this.generateStickerImage({ artwork: updatedArtwork, useCache: false });
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.trim-info {
  $this: &;

  display: flex;
  flex-direction: column;
  gap: 12px;

  &:empty {
    display: none;
  }

  &__wrapper {
    display: flex;
    gap: 4px;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    align-items: center;

    &--warn {
      color: $yellow-orange;
    }

    &--error {
      color: $red-light;
    }
  }

  &__icon {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  &__segment-button {
    appearance: none;
    border: none;
    cursor: pointer;
    flex-shrink: 0;
    border-radius: 8px;
    padding: 10px 16px;
    min-width: 150px;
    font-size: 14px;
    line-height: 1.42;
    font-weight: 700;
    width: 100%;

    &--annotate {
      background: $primary-light;
      color: $white;
      margin-right: 2%;
    }

    &--cancel {
      color: $info;
      text-align: center;
      text-decoration: none;
      border: 1px solid $gray-border;
      background-color: $white;
    }

    &--half-width {
      width: 49%;
    }
  }

  &__undo-button,
  &__redo-button {
    width: 30px;
    height: 30px;
  }

  &__undo {
    width: 100%;
    height: 100%;
    transform: rotate(90deg);
  }

  &__redo {
    width: 100%;
    height: 100%;
    transform: rotate(270deg);
  }

  &__remove-toggle {
    border: none;
    background: none;
    padding: 0;
    display: flex;
    align-items: center;
    gap: 8px;

    &--selected {
      #{$this}__remove-toggle-icon {
        background-color: $primary-light;
        border-color: $primary-light;
      }
    }

    &--disabled {
      opacity: 0.5;
    }
  }

  &__remove-toggle-icon {
    width: 24px;
    height: 24px;
    color: $white;
    background-color: $white;
    border: 2px solid $app-gray-modern-400;
    border-radius: 4px;
  }

  &__remove-toggle-label {
    color: $black-light;
    font-size: 15px;
    font-weight: 700;
  }
}
</style>
